import React, { useState, useMemo } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import gfm from 'remark-gfm';
import CodeBlock from './CodeBlock';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";

interface IntelligentChatMessageProps {
  id: string;
  user: string;
  message: string;
  color: string;
  /** When true, skip [UserContextData] parsing and only render markdown (e.g. for AgentChat). */
  skipUserContextData?: boolean;
}

export const IntelligentChatMessage: React.FC<IntelligentChatMessageProps> = ({ id, user, message, color, skipUserContextData = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  // Typisierte Custom-Komponenten für Markdown
  const components: Components = useMemo(() => ({
    // Verwende die separate CodeBlock-Komponente für das Rendern von Code
    code: ({ node, inline, className, children, ...props }) => {
      if (inline) {
        return <code className={className} {...props}>{children}</code>;
      }

      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      const code = String(children).replace(/\n$/, '');

      // Extrahiere eine eindeutige ID für den Codeblock
      const codeBlockId = `${id}-code`;

      return (
        <CodeBlock
          id={codeBlockId}
          language={language}
          code={code}
        />
      );
    },
  }), []); // Empty dependency array if no external dependencies


  const reactMarkdown = useMemo(() => (
    <ReactMarkdown
      className="chatMessage"
      remarkPlugins={[gfm]}
      components={components}
    >
      {message}
    </ReactMarkdown>
  ), [message, components]);

  const regex = /\[UserContextData(?:\s+title="([^"]+)")?\]([\s\S]*?)\[\/UserContextData\]/;
  const match = !skipUserContextData && message.match(regex);

  if (match) {
    const title = match[1] || "Mein Kontext";
    const contextData = match[2];
    const restOfMessage = message.replace(regex, '').trim();

    return (
      <div id={id}>
        <span style={{ color }}>{user}</span>:&nbsp;
        <ReactMarkdown
          className="chatMessage"
          remarkPlugins={[gfm]}
          components={components}
        >
          {restOfMessage}
        </ReactMarkdown>
        <div className="user-context-data">
          <button onClick={toggleExpand} className="context-toggle">
            {isExpanded ? `"${title}" zuklappen` : title}&nbsp;
            <FontAwesomeIcon icon={isExpanded ? faCaretUp : faCaretDown} />
          </button>
          {isExpanded && (
            <div className="context-content">
              <ReactMarkdown
                remarkPlugins={[gfm]}
                components={components}
              >
                {contextData}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div id={id}>
      <span style={{ color }}>{user}</span>:&nbsp;
      {reactMarkdown}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const IntelligentChatMessageMemo = React.memo(IntelligentChatMessage);